import {
	Backdrop,
	Box,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Stack,
	Paper,
	Typography,
	TextField,
	InputAdornment,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import DehazeIcon from '@mui/icons-material/Dehaze';
import Main from "components/Main/Main";
import ReservedForm from "components/ReservedForm";
import useFlagBagController from "hooks/useFlagBagController";
import useFlagBagLogController from "hooks/useFlagBagLogController";
import { enqueueSnackbar } from "notistack";
import SyncBar from "components/Common/SyncBar";
import React from "react";
import { useData } from "store/DataProvider";
import {
	FlagBag,
	FlagBagStatus,
	StatusAllowCollect,
	StatusCode,
	StatusCollected
} from "services/api/flagbags/types";
import { flagBagApi } from "services/api/flagbags";
import { ConflictSelectModals } from "./components/ConflictSelectModals";
import { StatusUpdateModals } from "./components/StatusUpdateModals";
import { DataGrid, GridRenderCellParams, type GridSortModel } from "@mui/x-data-grid";
import { usePendingQueue } from "hooks/usePendingQueue";
import { loadConflictsFromStorage, ConflictItem } from "utils/dataConflictDetector";
import { initDB } from "react-indexed-db-hook";
import { DBConfig } from "DBConfig";
import { StatusLabel } from "types/flagbag";
import { useNetworkStatus } from "hooks/useNetworkStatus";
import { getAllFlagbags } from "utils/flagbagUtils";
import { TagChip } from "./components/tag-chip";
import { getNormalColumns, getPendingProcessColumns, getPendingSyncColumns } from "./components/flagbag-list-columns";
import { useVolunteerController } from "hooks/useVolunteerController";
import { VolunteerData } from "services/api/volunteer/types";
import { initIndexedDbIfMissingStore } from "utils/indexedDbRecovery";
import { FLAGBAG_STATUS_SAVED_EVENT } from "utils/flagbagStatusEvents";
import {
	convertApiStatusToStatusCode,
	convertStatusCodeToApiCode,
	convertStatusCodeToValue,
	getAllowedStatuses,
	getComingStatusListLocal,
	isReservedFlagbag,
} from "utils/flagbagStatusUtils";

/** Multiple queue rows per bag: pick latest by syncFailedAt / updatedAt / OfflineData.id (UUIDv7). */
function pendingOfflineActionTimeScore(action: { syncFailedAt?: number; updatedAt?: string }): number {
	if (typeof action.syncFailedAt === "number") return action.syncFailedAt;
	const ut = action.updatedAt;
	if (ut != null && ut !== "") {
		const n = parseFloat(String(ut));
		if (!Number.isNaN(n)) return n < 1e11 ? n * 1000 : n;
	}
	return 0;
}

function isPendingOfflineActionNewer(a: any, b: any): boolean {
	const ta = pendingOfflineActionTimeScore(a);
	const tb = pendingOfflineActionTimeScore(b);
	if (ta !== tb) return ta > tb;
	const ida = String(a?.id ?? "");
	const idb = String(b?.id ?? "");
	return ida > idb;
}

function matchesFlagBagSearch(flagbag: any, search?: string): boolean {
	const keyword = search?.trim().toLowerCase();
	if (!keyword) return true;

	return [
		flagbag.flagbagNum,
		flagbag.flagBagNum,
		flagbag.volunteerName,
		flagbag.volunteerEmail,
		flagbag.email,
		flagbag.id,
		flagbag.stationName,
		flagbag.finalStationName,
		flagbag.finalStationNum,
		flagbag.finalStationId,
	].some((value) => String(value ?? "").toLowerCase().includes(keyword));
}

/** 多收：所屬旗站 ≠ 當前站；少收：回收旗站 ≠ 當前站；後備：`bagType === "spare"`。複選時後備與站點條件為 AND。 */
function matchesCollectStationFilter(
	flagbag: any,
	currentStationId: string,
	includeOver: boolean,
	includeUnder: boolean,
	includeSpare: boolean,
): boolean {
	if (!includeOver && !includeUnder && !includeSpare) return true;

	const isSpare = String(flagbag?.bagType ?? "").toLowerCase() === "spare";

	let collectOk = true;
	if (includeOver || includeUnder) {
		const cur = String(currentStationId ?? "").trim();
		const assigned = String(flagbag?.stationId ?? "").trim();
		const finalId = String(flagbag?.finalStationId ?? "").trim();
		const isOver = assigned !== "" && cur !== "" && assigned !== cur;
		const isUnder = finalId !== "" && cur !== "" && finalId !== cur;
		if (includeOver && includeUnder) collectOk = isOver || isUnder;
		else if (includeOver) collectOk = isOver;
		else collectOk = isUnder;
	}

	if (includeSpare && !includeOver && !includeUnder) return isSpare;
	if (!includeSpare) return collectOk;
	return isSpare && collectOk;
}

const DEFAULT_SORT_MODEL: GridSortModel = [{ field: "flagbagNum", sort: "asc" }];
const UNKNOWN_STATUS_SORT_INDEX = Number.MAX_SAFE_INTEGER;

function getStatusSortIndexFromOptions(
	statusCode: StatusCode | string | undefined,
	statusOptions: Array<{ code: StatusCode }>,
): number {
	if (!statusCode) return UNKNOWN_STATUS_SORT_INDEX;
	const index = statusOptions.findIndex((opt) => opt.code === statusCode);
	return index >= 0 ? index : UNKNOWN_STATUS_SORT_INDEX;
}

function getFlagBagSortValue(
	row: any,
	field: string,
	resolveStatusSortIndex?: (status: StatusCode | string | undefined) => number,
): unknown {
	switch (field) {
		case "flagbagNum":
			return row.flagbagNum ?? row.flagBagNum;
		case "status": {
			const statusCode = row.currentStatus ?? row.status;
			if (resolveStatusSortIndex) {
				return resolveStatusSortIndex(statusCode);
			}
			return statusCode;
		}
		default:
			return row[field];
	}
}

function compareSortValues(a: unknown, b: unknown): number {
	const aEmpty = a === null || a === undefined || a === "";
	const bEmpty = b === null || b === undefined || b === "";
	if (aEmpty && bEmpty) return 0;
	if (aEmpty) return 1;
	if (bEmpty) return -1;

	if (typeof a === "number" && typeof b === "number") {
		return a - b;
	}

	return String(a).localeCompare(String(b), undefined, {
		numeric: true,
		sensitivity: "base",
	});
}

function sortRowsBySortModel<T extends object>(
	rows: T[],
	sortModel: GridSortModel,
	resolveStatusSortIndex?: (status: StatusCode | string | undefined) => number,
): T[] {
	const sortItem = sortModel[0];
	if (!sortItem?.field || !sortItem.sort) {
		return rows;
	}

	const direction = sortItem.sort === "desc" ? -1 : 1;
	return [...rows].sort((a, b) => {
		return direction * compareSortValues(
			getFlagBagSortValue(a as any, sortItem.field, resolveStatusSortIndex),
			getFlagBagSortValue(b as any, sortItem.field, resolveStatusSortIndex),
		);
	});
}

const FlagBagList = () => {
	const data = useData();
	const { isOnline } = useNetworkStatus();
	const FlagBagController = useFlagBagController();
	const { logStatusUpdate } = useFlagBagLogController();
	const volunteerController = useVolunteerController();
	const offlineDB = initIndexedDbIfMissingStore("offline");
	const flagBagDB = initIndexedDbIfMissingStore("flagbag");
	const hasWarnedOfflineStoreMissingRef = React.useRef(false);
	const hasTriedStoreRecoveryRef = React.useRef(false);
	const [isLoading, setIsLoading] = React.useState(false);
	const [isInit, setIsInit] = React.useState(false);

	const isOfflineStoreMissingError = React.useCallback((error: unknown) => {
		const errorMessage = error instanceof Error ? error.message : String(error ?? "");
		return (
			errorMessage.includes("objectStore does not exists: offline") ||
			errorMessage.includes("objectStore does not exists: flagbag") ||
			errorMessage.includes("One of the specified object stores was not found")
		);
	}, []);

	const recoverMissingStoreIfNeeded = React.useCallback(async () => {
		if (hasTriedStoreRecoveryRef.current) {
			return false;
		}
		hasTriedStoreRecoveryRef.current = true;
		try {
			initDB(DBConfig);
			// Wait a tick for IDB upgrade transaction to finish.
			await new Promise((resolve) => setTimeout(resolve, 120));
			console.warn("[IndexedDB] Missing store detected. Retried initDB to create missing stores.");
			return true;
		} catch (error) {
			console.error("[IndexedDB] Failed to recover missing store.", error);
			return false;
		}
	}, []);

	const getOfflinePendingActionsSafe = React.useCallback(async () => {
		try {
			return await offlineDB.getAll();
		} catch (error) {
			if (isOfflineStoreMissingError(error)) {
				const recovered = await recoverMissingStoreIfNeeded();
				if (recovered) {
					try {
						return await offlineDB.getAll();
					} catch (retryError) {
						console.error("[IndexedDB] Retry getAll after store recovery failed.", retryError);
					}
				}
				if (!hasWarnedOfflineStoreMissingRef.current) {
					hasWarnedOfflineStoreMissingRef.current = true;
					console.warn("[IndexedDB] `offline` store missing. Fallback to empty pending actions.", error);
					enqueueSnackbar("本地離線資料庫異常，待同步資料暫不可用。", { variant: "warning" });
				}
				return [];
			}
			throw error;
		}
	}, [offlineDB, isOfflineStoreMissingError, recoverMissingStoreIfNeeded]);
	
	// Modal stack system
	type ModalType = 'flagbagDetail' | 'conflictSelect' | 'statusUpdate' | 'reservedForm';
	
	interface ModalStackItem {
		type: ModalType;
		data?: any;
	}
	
	const [modalStack, setModalStack] = React.useState<ModalStackItem[]>([]);
	
	const openModal = (type: ModalType, data?: any) => {
		setModalStack(prev => [...prev, { type, data }]);
	};
	
	const closeModal = () => {
		setModalStack(prev => prev.slice(0, -1));
	};
	
	const closeAllModals = () => {
		setModalStack([]);
	};
	
	
	const [selectedFlagBag, setSelectedFlagBag] = React.useState<{ flagBagId: string; flagbagNum: string }>(null);
	const [editingFlagBag, setEditingFlagBag] = React.useState<{
		id: string;
		flagbagNum: string;
		currentStatus: StatusCode;
	} | null>(null);

	const [flagBagDetail, setFlagBagDetail] = React.useState({
		volunteerName: "",
		volunteerStationName: "",
		teamName: "",
		phone: "",
		email: "",
		remarks: "",
		flagBagId: "",
		flagbagNum: "",
		serverStatus: "",
		localStatus: "",
		serverUpdateTime: "",
		serverUpdateBy: "",
	});

	// 状态代码到标签的映射（用于显示状态名称）
	const [statusLabelMap, setStatusLabelMap] = React.useState<Record<StatusCode, string>>({} as Record<StatusCode, string>);
	
	// 状态代码到 API code 的映射（用于传递状态给 API）
	const [statusCodeToApiCodeMap, setStatusCodeToApiCodeMap] = React.useState<Record<StatusCode, string>>({} as Record<StatusCode, string>);

	const onStatusCellClick = (params: GridRenderCellParams) => {
		console.log("onStatusCellClick", params);
		const statusValue = params.value || params.row.currentStatus || params.row.status;

		// Check if it's a spare bag with no volunteer assigned
		const isSpareBag = params.row.bagType === "spare";
		const hasNoVolunteer = !params.row.volunteerId || params.row.volunteerId === "0" || params.row.volunteerId === "";

		if (isSpareBag && hasNoVolunteer) {
			const flagBagStatus: FlagBagStatus = {
				flagBagId: params.row.id,
				currentStatus: (statusValue as StatusCode) || StatusCode.NOT_ASSIGN,
				flagbagNum: params.row.flagbagNum,
				volunteerName: params.row.volunteerName || "",
				allowedStatusList: params.row.allowedStatusList || [],
				bagType: params.row.bagType,
			};
			handleReservedBag(flagBagStatus, params.row.id, params.row.flagbagNum);
			return;
		}

		const statusCode: StatusCode = (statusValue as StatusCode) || StatusCode.NOT_ASSIGN;

		setEditingFlagBag({
			id: params.row.id,
			flagbagNum: params.row.flagbagNum,
			currentStatus: statusCode,
		});
		openModal("statusUpdate", {
			id: params.row.id,
			flagbagNum: params.row.flagbagNum,
			currentStatus: statusCode,
		});
	};

	const normalColumns = React.useMemo(() => getNormalColumns({ 
		onStatusCellClick,
		statusLabelMap: statusLabelMap,
	}), [onStatusCellClick, statusLabelMap]);
	const pendingSyncColumns = React.useMemo(
		() =>
			getPendingSyncColumns({
				onStatusCellClick,
				statusLabelMap: statusLabelMap,
			}),
		[onStatusCellClick, statusLabelMap],
	);
	const pendingProcessColumns = getPendingProcessColumns();

	type FlagBagType = FlagBagStatus & FlagBag;

	const [rowsAll, setRowsAll] = React.useState<Array<FlagBag>>([]);
	const [rowsPendingSync, setRowsPendingSync] = React.useState<Array<FlagBag>>([]);
	const [rowsPendingProcess, setRowsPendingProcess] = React.useState<Array<FlagBag>>([]);
	const [searchText, setSearchText] = React.useState("");
	const [apiSearchText, setApiSearchText] = React.useState("");
	const apiSearchTextRef = React.useRef("");
	const searchKeyupDebounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const [flagbagNum, setFlagbagNum] = React.useState("");
	const [paginationModel, setPaginationModel] = React.useState({
		page: 0,
		pageSize: 8,
	});
	const [sortModel, setSortModel] = React.useState<GridSortModel>(DEFAULT_SORT_MODEL);
	const [totalRowsAll, setTotalRowsAll] = React.useState(0);
	const [totalRowsPendingSync, setTotalRowsPendingSync] = React.useState(0);
	const [totalRowsPendingProcess, setTotalRowsPendingProcess] = React.useState(0);
	const [filterAllOvercollected, setFilterAllOvercollected] = React.useState(false);
	const [filterAllUndercollected, setFilterAllUndercollected] = React.useState(false);
	const [filterAllSpareFlagbag, setFilterAllSpareFlagbag] = React.useState(false);
	const filterAllOvercollectedRef = React.useRef(false);
	const filterAllUndercollectedRef = React.useRef(false);
	const filterAllSpareFlagbagRef = React.useRef(false);
	filterAllOvercollectedRef.current = filterAllOvercollected;
	filterAllUndercollectedRef.current = filterAllUndercollected;
	filterAllSpareFlagbagRef.current = filterAllSpareFlagbag;
	const [isLoadingPage, setIsLoadingPage] = React.useState(false);
	const [selectedTag, setSelectedTag] = React.useState<string[]>([]);
	const [statusOptions, setStatusOptions] = React.useState<Array<{
		label: string;
		value: string;
		code: StatusCode;
		hidden?: boolean;
	}>>([
		{ label: "已分配/未出", value: "ASSIGNED", code: StatusCode.ASSIGNED },
		{ label: "已分發/未收", value: "DISTRIBUTED", code: StatusCode.DISTRIBUTED },
		{ label: "已分發（其他）/未收", value: "DISTRIBUTED_OTHER", code: StatusCode.DISTRIBUTED_OTHER },
		{ label: "實收袋", value: "COLLECTED", code: StatusCode.COLLECTED },
		{ label: "實收袋&入銀行", value: "BANK", code: StatusCode.BANK },
		{ label: "收吉袋", value: "COLLECTED_EMPTY", code: StatusCode.COLLECTED_EMPTY },
		{ label: "已收（其他）", value: "COLLECTED_OTHER", code: StatusCode.COLLECTED_OTHER },
		{ label: "遲交", value: "LATE", code: StatusCode.LATE },
		{ label: "遺失", value: "LOSS", code: StatusCode.LOSS },
	]);


	const { getPendingDataInfo, syncStatus, syncErrors } = usePendingQueue();
	const [pendingSyncCount, setPendingSyncCount] = React.useState(0);
	const [pendingProcessCount, setPendingProcessCount] = React.useState(0);
	const [pendingSyncIds, setPendingSyncIds] = React.useState<Set<string>>(new Set());
	const [pendingProcessIds, setPendingProcessIds] = React.useState<Set<string>>(new Set());

	const refreshPendingTabCounts = React.useCallback(async () => {
		try {
			const pendingSyncIdsSet = new Set<string>();
			try {
				const allPendingActions = await getOfflinePendingActionsSafe();
				if (allPendingActions && Array.isArray(allPendingActions)) {
					allPendingActions.forEach((action: any) => {
						if (action.flagBagID && !action.syncError) {
							pendingSyncIdsSet.add(action.flagBagID);
						}
					});
				}
			} catch (error) {
				console.error("获取待同步数据失败:", error);
			}
			setPendingSyncIds(pendingSyncIdsSet);
			setPendingSyncCount(pendingSyncIdsSet.size);

			const pendingProcessIdsSet = new Set<string>();
			const conflicts = loadConflictsFromStorage();
			if (conflicts && Array.isArray(conflicts)) {
				conflicts.forEach((conflict: ConflictItem) => {
					if (conflict.flagBagId) {
						pendingProcessIdsSet.add(conflict.flagBagId);
					}
				});
			}
			try {
				const allPendingActions = await getOfflinePendingActionsSafe();
				if (allPendingActions && Array.isArray(allPendingActions)) {
					allPendingActions.forEach((action: any) => {
						if (action.flagBagID && action.syncError) {
							pendingProcessIdsSet.add(action.flagBagID);
						}
					});
				}
			} catch (error) {
				console.error("获取同步失败数据失败:", error);
			}
			setPendingProcessIds(pendingProcessIdsSet);
			setPendingProcessCount(pendingProcessIdsSet.size);
		} catch {
			// Silent fail
		}
	}, [getOfflinePendingActionsSafe]);

	const refreshPendingTabCountsRef = React.useRef(refreshPendingTabCounts);
	React.useLayoutEffect(() => {
		refreshPendingTabCountsRef.current = refreshPendingTabCounts;
	});

	const tabs = [
		{ label: "全部旗袋", value: "ALL", color: "#269FFF", count: undefined },
		{ label: "待同步旗袋", value: "PENDING_SYNC", color: "#FF8914", count: pendingSyncCount },
		{ label: "待處理旗袋", value: "PENDING_PROCESS", color: "#FF2626", count: pendingProcessCount },
	];

	const [selectedTab, setSelectedTab] = React.useState<string>("ALL");
	const selectedTabRef = React.useRef(selectedTab);
	selectedTabRef.current = selectedTab;
	const paginationModelRef = React.useRef(paginationModel);
	paginationModelRef.current = paginationModel;
	const sortModelRef = React.useRef<GridSortModel>(sortModel);
	sortModelRef.current = sortModel;
	const statusOptionsRef = React.useRef(statusOptions);
	statusOptionsRef.current = statusOptions;

	const rowsForSelectedTab =
		selectedTab === "PENDING_SYNC"
			? rowsPendingSync
			: selectedTab === "PENDING_PROCESS"
				? rowsPendingProcess
				: rowsAll;

	React.useEffect(() => {
		void refreshPendingTabCounts();
	}, [refreshPendingTabCounts, getPendingDataInfo, syncStatus, syncErrors]);

	// tabs use pendingSyncCount / pendingProcessCount; FLAGBAG_STATUS_SAVED also refreshes counts

	function filterOptions(status: StatusCode) {
		const allowedStatuses = getAllowedStatuses(status);
		return statusOptions.map((option) => {
			const isCurrentStatus = option.code === status;
			const isAllowed = isCurrentStatus || allowedStatuses.includes(option.code);
			return {
				...option,
				disabled: !isAllowed && !option.hidden,
			};
		});
	}

	// 获取状态标签，优先使用 API 返回的映射
	function getStatusLabel(statusCode: StatusCode | string | undefined): string {
		if (!statusCode) return "-";
		// 优先使用 API 返回的状态标签映射
		if (statusLabelMap[statusCode as StatusCode]) {
			return statusLabelMap[statusCode as StatusCode];
		}
		// 降级使用默认的 StatusLabel
		return StatusLabel[statusCode as StatusCode] || "-";
	}

	const getStatusSortIndex = (statusCode: StatusCode | string | undefined) =>
		getStatusSortIndexFromOptions(statusCode, statusOptionsRef.current);

	// 加载状态列表
	React.useEffect(() => {
		const loadStatuses = async () => {
			try {
				const eventId = data.getEventId();
				if (!eventId) {
					return;
				}

				const statuses = await flagBagApi.getStatuses(eventId);
				
				// 将 API 返回的状态转换为 statusOptions 格式
				const convertedOptions = statuses.map((status) => {
					const statusCode = convertApiStatusToStatusCode(status.code);
					return {
						label: status.labelTc, // 使用繁体中文标签
						value: convertStatusCodeToValue(status.code),
						code: statusCode,
						hidden: status.code === 'unassigned',
					};
				});

				setStatusOptions(convertedOptions);

				// 创建状态代码到标签的映射
				const labelMap: Record<StatusCode, string> = {} as Record<StatusCode, string>;
				// 创建状态代码到 API code 的映射
				const codeMap: Record<StatusCode, string> = {} as Record<StatusCode, string>;
				// 遍历 API 返回的所有状态，建立映射关系
				statuses.forEach((status) => {
					const statusCode = convertApiStatusToStatusCode(status.code);
					// 使用 API 返回的 labelTc 作为显示标签
					labelMap[statusCode] = status.labelTc;
					codeMap[statusCode] = status.code; // 保存 API code
				});
				// 确保所有可能的状态都有映射（即使 API 没有返回，也要使用默认标签作为后备）
				// 特别处理 unassigned 状态，因为它在 statusOptions 中被隐藏了，但可能仍然存在于数据中
				if (!labelMap[StatusCode.NOT_ASSIGN]) {
					// 查找 API 返回中是否有 unassigned 状态
					const unassignedStatus = statuses.find(s => s.code === 'unassigned');
					if (unassignedStatus) {
						labelMap[StatusCode.NOT_ASSIGN] = unassignedStatus.labelTc;
					} else {
						// 如果 API 没有返回，使用默认标签作为后备
						labelMap[StatusCode.NOT_ASSIGN] = StatusLabel[StatusCode.NOT_ASSIGN] || "未分配";
					}
				}
				setStatusLabelMap(labelMap);
				setStatusCodeToApiCodeMap(codeMap);
			} catch (error) {
				console.error("加载状态列表失败:", error);
				// 如果加载失败，使用默认的状态选项
			}
		};

		loadStatuses();
	}, [data.getEventId()]);



	const handleSearch = (searchText: string, tags: string[] = selectedTag) => {
		setSearchText(searchText);
	};
	const modifyFlagBagStatus = async (flagBag: FlagBagType, status: StatusCode, isFreeUpdate: boolean = true) => {
		if (isReservedFlagbag(flagBag)) {
			handleReservedBag(flagBag, flagBag.id, flagBag.flagbagNum);
			throw new Error("RESERVED_BAG");
		}

		// 将 StatusCode 转换为 API code
		const apiCode = statusCodeToApiCodeMap[status] || convertStatusCodeToApiCode(status);
		// 将当前状态转换为 API code
		const currentApiCode = statusCodeToApiCodeMap[flagBag.currentStatus] || convertStatusCodeToApiCode(flagBag.currentStatus);
		const resp = await FlagBagController.updateFlagBagStatusToServerAdvance(
			flagBag.id, 
			apiCode, 
			currentApiCode, 
			isFreeUpdate
		);

		// Validate response structure
		if (!resp || !resp.updatedFlagbags || !resp.detail) {
			enqueueSnackbar("更新失敗：伺服返回無效數據", { variant: "error" });
			throw new Error("Invalid response structure");
		}

		const flagbagNumber = resp.detail.flagbagNum || resp.detail.flagBagNum || flagBag.flagbagNum;
		const updatedStatus = getStatusLabel(status);

		enqueueSnackbar(
			<Box>
				<Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
					已修改為：{updatedStatus}
				</Typography>
				<Typography sx={{ fontSize: "12px", color: "rgba(255, 255, 255, 0.9)" }}>
					旗袋編號：{flagbagNumber}
				</Typography>
			</Box>,
			{
				variant: "success",
				style: {
					display: "flex",
					flexDirection: "column",
					gap: "8px",
					alignItems: "center",
					justifyContent: "center",
					backgroundColor: "#3DBE02",
					color: "#fff",
					borderRadius: "12px",
					border: "1px solid #3DBE02",
				},
			});

		return resp;
	};

	const handleReservedBag = (flagBag: FlagBagStatus, flagBagId: string, flagbagNum: string) => {
		setSelectedFlagBag({ flagBagId: flagBagId, flagbagNum: flagbagNum });
		openModal("reservedForm", { flagBagId, flagbagNum });
	};
	const handleSubmit = async (data) => {
		try {
			// 获取当前旗袋状态，判断应该显示什么消息
			const currentBag = await FlagBagController.getBag(selectedFlagBag.flagBagId);
			const currentStatus = currentBag?.status || currentBag?.currentStatus;
			const isUnassigned = currentStatus === StatusCode.NOT_ASSIGN;
			
			await FlagBagController.checkOutSpareFlagBag(selectedFlagBag.flagBagId, data.volunteerId);
			
			// 根据状态显示不同的消息
			const message = isUnassigned 
				? `旗袋 ${selectedFlagBag.flagbagNum} 已分配給義工`
				: `旗袋 ${selectedFlagBag.flagbagNum} 已更新(未收)`;
			enqueueSnackbar(message, { variant: "success" });
			// Reload page data to reflect the changes
			await loadPageData(paginationModel.page, paginationModel.pageSize, undefined, apiSearchText);
			// Refresh pending sync/process tab counts (needed for offline: checkOutSpareFlagBag
			// adds to the offline DB but does not update the tab badge counts)
			await refreshPendingTabCounts();
		} catch (error: any) {
			console.error("更新旗袋失敗:", error);
			// 显示错误消息，优先使用 API 返回的 message，否则显示默认消息
			const errorMsg = error?.errorMsg || error?.message || "更新失敗";
			const errorCode = error?.errorCode !== undefined ? `${error.errorCode}: ` : "";
			enqueueSnackbar(errorCode + errorMsg, { variant: "error" });
		}
	};


	const noSuchBag = (flagbagNum?: string) => {
		const message = flagbagNum 
			? `沒有此旗袋\n旗袋編號：${flagbagNum}`
			: "沒有此旗袋";
		enqueueSnackbar(message, { variant: "error" });
		
	};
	
	const handleStatusChangeWithCode = async (flagBagId: string, statusCode: StatusCode) => {
		// 如果離線，直接從本地數據庫獲取數據
		if (!isOnline && selectedTab !== "PENDING_PROCESS") {
			const flagbag = await FlagBagController.getBag(flagBagId);
			
			if (!flagbag) {
				noSuchBag();
				return;
			}

			// 確保 status 字段存在，如果不存在則使用 currentStatus
			const currentStatus = flagbag.status ?? flagbag.currentStatus;
			
			// 如果狀態仍然是 undefined，顯示錯誤
			if (currentStatus === undefined || currentStatus === null) {
				enqueueSnackbar(`旗袋狀態錯誤\n旗袋編號：${flagbag.flagbagNum || flagBagId}`, { variant: "error" });
				return;
			}
			let pendingStatus = undefined;

			try {
				const pendingActions = await getOfflinePendingActionsSafe();
				if (pendingActions && Array.isArray(pendingActions)) {
					const targetPending = pendingActions.find((action: any) => action.flagBagID === flagBagId && action.status !== undefined);
					if (targetPending) {
						pendingStatus = targetPending.status;
					}
				}
			} catch (error) {
				console.warn("讀取 Pending Action 錯誤", error);
			}
			console.log(pendingStatus, currentStatus, statusCode);
			if (
				Object.values(StatusCollected).includes(statusCode as unknown as StatusCollected) &&
				!Object.values(StatusCollected).includes(currentStatus as unknown as StatusCollected)
			) {
				if (
					(pendingStatus && 
						(!Object.values(StatusAllowCollect).includes(statusCode as unknown as StatusAllowCollect)) ||
					    !Object.values(StatusAllowCollect).includes(currentStatus as unknown as StatusAllowCollect))
				) {
					enqueueSnackbar(`請先將旗袋 ${flagbag.flagbagNum || flagBagId} 設定為未收`, { variant: "warning" });
					return;
				}

			}
			// 將操作添加到離線數據庫
			await FlagBagController.addOfflineDataIndividual(flagBagId, statusCode, false);
			
			// 更新本地狀態
			const existingBag = await flagBagDB.getByID(flagBagId);
			await flagBagDB.update({ ...existingBag, status: statusCode, currentStatus: statusCode });
			await logStatusUpdate(flagBagId, statusCode, data.getStationId());

			// Update counts based on unique flagBagIds
			const pendingSyncIdsSet = new Set<string>();
			try {
				const allPendingActions = await getOfflinePendingActionsSafe();
				if (allPendingActions && Array.isArray(allPendingActions)) {
					allPendingActions.forEach((action: any) => {
						if (action.flagBagID && !action.syncError) {
							pendingSyncIdsSet.add(action.flagBagID);
						}
					});
				}
			} catch (error) {
				console.error("获取待同步数据失败:", error);
			}
			setPendingSyncIds(pendingSyncIdsSet);
			setPendingSyncCount(pendingSyncIdsSet.size);

			const pendingProcessIdsSet = new Set<string>();
			const conflicts = loadConflictsFromStorage();
			if (conflicts && Array.isArray(conflicts)) {
				conflicts.forEach((conflict: ConflictItem) => {
					if (conflict.flagBagId) {
						pendingProcessIdsSet.add(conflict.flagBagId);
					}
				});
			}
			try {
				const allPendingActions = await getOfflinePendingActionsSafe();
				if (allPendingActions && Array.isArray(allPendingActions)) {
					allPendingActions.forEach((action: any) => {
						if (action.flagBagID && action.syncError) {
							pendingProcessIdsSet.add(action.flagBagID);
						}
					});
				}
			} catch (error) {
				console.error("获取同步失败数据失败:", error);
			}
			setPendingProcessIds(pendingProcessIdsSet);
			setPendingProcessCount(pendingProcessIdsSet.size);

			await loadPageData(paginationModel.page, paginationModel.pageSize, undefined, apiSearchText);

			// 顯示離線記錄提示
			enqueueSnackbar(`離線紀錄\n旗袋編號：${flagbag.flagbagNum || flagBagId}`, { variant: "warning" });
			return;
		}

		setIsLoading(true);
		const row = rowsForSelectedTab.find((flagBag) => flagBag.id === flagBagId);
		try {
			await modifyFlagBagStatus(row as FlagBagType, statusCode);

			closeModal();

			try {
				const pendingSyncIdsSet = new Set<string>();
				const allPendingActions = await getOfflinePendingActionsSafe();
				if (allPendingActions && Array.isArray(allPendingActions)) {
					allPendingActions.forEach((action: any) => {
						if (action.flagBagID && !action.syncError) {
							pendingSyncIdsSet.add(action.flagBagID);
						}
					});
				}
				setPendingSyncIds(pendingSyncIdsSet);
				setPendingSyncCount(pendingSyncIdsSet.size);

				const pendingProcessIdsSet = new Set<string>();
				const conflicts = loadConflictsFromStorage();
				if (conflicts && Array.isArray(conflicts)) {
					conflicts.forEach((conflict: ConflictItem) => {
						if (conflict.flagBagId) {
							pendingProcessIdsSet.add(conflict.flagBagId);
						}
					});
				}
				if (allPendingActions && Array.isArray(allPendingActions)) {
					allPendingActions.forEach((action: any) => {
						if (action.flagBagID && action.syncError) {
							pendingProcessIdsSet.add(action.flagBagID);
						}
					});
				}
				setPendingProcessIds(pendingProcessIdsSet);
				setPendingProcessCount(pendingProcessIdsSet.size);
			} catch (error) {
				console.error("更新计数失败:", error);
			}

			await loadPageData(paginationModel.page, paginationModel.pageSize, undefined, apiSearchText);
		} catch (error) {
			if (error?.message !== "RESERVED_BAG") {
				console.error("修改状态失败:", error);
				const errorMsg = error?.errorMsg || error?.message || "更新失敗";
				const errorCode = error?.errorCode !== undefined ? `${error.errorCode}: ` : "";
				enqueueSnackbar(errorCode + errorMsg, { variant: "error" });
			}
		} finally {
			setIsLoading(false);
		}
	};


	const handleRefresh = async (syncData) => {
		try {
			setIsLoading(true);
			const stationId = data.getStationId();

			if (stationId) {
				if (syncData && syncData.flagBags) {
					await loadPageData(paginationModel.page, paginationModel.pageSize, undefined, apiSearchText);
					enqueueSnackbar("數據已同步", { variant: "success" });
				} else {
					const freshData = await FlagBagController.initDB(stationId);
					if (freshData) {
						await loadPageData(paginationModel.page, paginationModel.pageSize, undefined, apiSearchText);
						enqueueSnackbar("數據已更新", { variant: "success" });
					}
				}
			}
		} catch (error) {
			enqueueSnackbar("更新失敗", { variant: "error" });
		} finally {
			setIsLoading(false);
		}
	};

	const loadPageData = async (
		pageNo: number,
		pageSize: number,
		filterType?: string,
		search?: string,
		sort: GridSortModel = sortModelRef.current,
	) => {
		try {
			console.log("load page data");
			const stationId = data.getStationId();
			const eventId = data.getEventId();

			if (!stationId) {
				enqueueSnackbar("缺少必要參數", { variant: "error" });
				return;
			}

			setIsLoadingPage(true);

			const selectedOption = (filterType || selectedTab || "ALL") as
				| "ALL"
				| "PENDING_SYNC"
				| "PENDING_PROCESS";

			const setPageRows = (
				tab: "ALL" | "PENDING_SYNC" | "PENDING_PROCESS",
				convertedFlagbags: Array<FlagBag>,
				total: number,
			) => {
				switch (tab) {
					case "ALL":
						setRowsAll(convertedFlagbags);
						setTotalRowsAll(total);
						break;
					case "PENDING_SYNC":
						setRowsPendingSync(convertedFlagbags);
						setTotalRowsPendingSync(total);
						break;
					case "PENDING_PROCESS":
						setRowsPendingProcess(convertedFlagbags);
						setTotalRowsPendingProcess(total);
						break;
				}
			};

			const conflicts = loadConflictsFromStorage() || [];
			const conflictMap = new Map(conflicts.map(c => [c.flagBagId, c]));

			// IMPORTANT:
			// For PENDING_* tabs, do NOT rely on React state (pendingSyncIds/pendingProcessIds) here,
			// because state can be stale right after a sync completes. Always derive IDs from the latest
			// IndexedDB snapshot we read in this function.
			const pendingSyncIdsFromDb = new Set<string>();
			const pendingProcessIdsFromDb = new Set<string>();
			const syncErrorMap = new Map<string, any>();

			let allPendingActions: any[] = [];
			try {
				allPendingActions = await getOfflinePendingActionsSafe();
			} catch (error) {
				console.error("获取同步失败数据失败:", error);
			}

			if (allPendingActions && Array.isArray(allPendingActions)) {
				allPendingActions.forEach((action: any) => {
					if (!action?.flagBagID) return;

					if (action.syncError) {
						pendingProcessIdsFromDb.add(action.flagBagID);
						syncErrorMap.set(action.flagBagID, action);
						return;
					}

					pendingSyncIdsFromDb.add(action.flagBagID);
				});
			}

			const latestPendingActionByBagId = new Map<string, any>();
			const pendingQueuedStatusByBagId = new Map<string, StatusCode | string>();
			if (allPendingActions && Array.isArray(allPendingActions)) {
				console.log("allPendingActions", allPendingActions);
				allPendingActions.forEach((action: any) => {
					if (!action?.flagBagID || action.status === undefined || action.status === null) return;
					const prev = latestPendingActionByBagId.get(action.flagBagID);
					if (""+(action.updatedAt || "0") > (""+(prev?.updatedAt || "0"))) {
						console.log("latest action", action);
						latestPendingActionByBagId.set(action.flagBagID, action);
					}
				});
				latestPendingActionByBagId.forEach((action, bagId) => {
					pendingQueuedStatusByBagId.set(bagId, action.status);
				});
			}

			const latestVolunteerOfflineActionByBagId = new Map<string, any>();
			if (allPendingActions && Array.isArray(allPendingActions)) {
				allPendingActions.forEach((action: any) => {
					if (!action?.flagBagID || !(action.name || action.phone)) return;
					const prev = latestVolunteerOfflineActionByBagId.get(action.flagBagID);
					if (!prev || isPendingOfflineActionNewer(action, prev)) {
						latestVolunteerOfflineActionByBagId.set(action.flagBagID, action);
					}
				});
			}

			const volunteerById = new Map<string, VolunteerData>();
			if (eventId) {
				try {
					const cachedVolunteers = await volunteerController.getVolunteersFromCache(eventId);
					cachedVolunteers.forEach((v) => volunteerById.set(v.volunteerId, v));
				} catch {
					// ignore cache read errors
				}
			}

			/**
			 * Volunteer display: offline queue rows (error → pending → any row with name/phone by flagBagID),
			 * then volunteer IndexedDB cache by volunteerId, then flagbag.
			 */
			const volunteerDisplayFromOfflineDb = (
				bagId: string,
				fb: { volunteerName?: string; volunteerPhone?: string; volunteerId?: string },
				fbAny: { volunteerPhone?: string; volunteerId?: string },
			) => {
				const errorAction = syncErrorMap.get(bagId);
				if (errorAction && (errorAction.name || errorAction.phone)) {
					return {
						volunteerName: errorAction.name || fb.volunteerName || "",
						volunteerPhone: errorAction.phone || fb.volunteerPhone || fbAny.volunteerPhone || "",
						volunteerId: String(
							errorAction.volunteerId ?? fb.volunteerId ?? fbAny.volunteerId ?? "",
						),
					};
				}
				const pendingAct = latestPendingActionByBagId.get(bagId);
				if (pendingAct && !pendingAct.syncError && (pendingAct.name || pendingAct.phone)) {
					return {
						volunteerName: pendingAct.name || fb.volunteerName || "",
						volunteerPhone: pendingAct.phone || fb.volunteerPhone || fbAny.volunteerPhone || "",
						volunteerId: String(
							pendingAct.volunteerId ?? fb.volunteerId ?? fbAny.volunteerId ?? "",
						),
					};
				}
				const fromScan = latestVolunteerOfflineActionByBagId.get(bagId);
				if (fromScan && (fromScan.name || fromScan.phone)) {
					return {
						volunteerName: fromScan.name || fb.volunteerName || "",
						volunteerPhone: fromScan.phone || fb.volunteerPhone || fbAny.volunteerPhone || "",
						volunteerId: String(
							fromScan.volunteerId ?? fb.volunteerId ?? fbAny.volunteerId ?? "",
						),
					};
				}

				const volunteerId =
					errorAction?.volunteerId ||
					pendingAct?.volunteerId ||
					fromScan?.volunteerId ||
					fbAny?.volunteerId ||
					fb?.volunteerId ||
					"";
				if (volunteerId) {
					const v = volunteerById.get(volunteerId);
					if (v) {
						return {
							volunteerName: v.name || fb.volunteerName || "",
							volunteerPhone: v.mobileNum || fb.volunteerPhone || fbAny.volunteerPhone || "",
							volunteerId: String(volunteerId),
						};
					}
				}
				return {
					volunteerName: fb.volunteerName || "",
					volunteerPhone: fb.volunteerPhone || fbAny.volunteerPhone || "",
					volunteerId: String(fb.volunteerId ?? fbAny.volunteerId ?? ""),
				};
			};

			// Conflicts are also "pending process"
			if (conflicts && Array.isArray(conflicts)) {
				conflicts.forEach((conflict: ConflictItem) => {
					if (conflict?.flagBagId) {
						pendingProcessIdsFromDb.add(conflict.flagBagId);
					}
				});
			}

			

			if (!eventId) {
				enqueueSnackbar("缺少活動ID", { variant: "error" });
				return;
			}

			if (selectedOption === "PENDING_SYNC" || selectedOption === "PENDING_PROCESS") {
				const idsToLoad = selectedOption === "PENDING_SYNC"
					? Array.from(pendingSyncIdsFromDb)
					: Array.from(pendingProcessIdsFromDb);

				const flagBagsPromises = idsToLoad.map(id => flagBagDB.getByID(id).catch(() => null));
				const flagBagsResults = await Promise.all(flagBagsPromises);

				let allFlagBags = flagBagsResults
					.filter(fb => fb != null)
					.filter(fb => matchesFlagBagSearch(fb, search));
				const startIndex = pageNo * pageSize;
				const endIndex = startIndex + pageSize;
				const convertedFlagbags = allFlagBags.map(fb => {
					const conflict = conflictMap.get(fb.id);
					const syncError = syncErrorMap.get(fb.id);
					const pendingQueuedStatus = latestPendingActionByBagId.get(fb.id)?.status;
					const baseCurrent = fb.currentStatus || fb.status;
					const currentStatus =
						!conflict && pendingQueuedStatus !== undefined && pendingQueuedStatus !== null
							? (pendingQueuedStatus as StatusCode)
							: baseCurrent;
					const vd = volunteerDisplayFromOfflineDb(fb.id, fb, fb as any);
					return {
						...fb,
						flagbagNum: fb.flagbagNum || fb.flagBagNum,
						currentStatus: currentStatus,
						status: currentStatus,
						volunteerPhone: vd.volunteerPhone,
						volunteerName: vd.volunteerName,
						volunteerId: vd.volunteerId,
						groupName: fb.teamName || fb.groupName || "",
						groupID: fb.teamId || fb.groupID || "",
						bagType: fb.bagType || "volunteer",
						finalStationId: fb.finalStationId || fb.stationId,
						finalStationName: fb.finalStationName || "",
						isCollectByOtherStation: fb.isCollectByOtherStation || false,
						isCollectForOtherStation: fb.isCollectForOtherStation || false,
						allowedStatusList: fb.allowedStatusList || [],
						serverStatus: conflict ? (getStatusLabel(conflict.serverStatus as StatusCode) || conflict.serverStatus) : (syncError ? "同步失敗" : "-"),
						localStatus: conflict
							? (getStatusLabel(conflict.localStatus as StatusCode) || conflict.localStatus)
							: currentStatus
								? getStatusLabel(currentStatus as StatusCode)
								: "-",
					};
				});
				const paginatedFlagbags = sortRowsBySortModel(convertedFlagbags, sort, getStatusSortIndex).slice(startIndex, endIndex);
				setPageRows(selectedOption, paginatedFlagbags, allFlagBags.length);
			} else if (selectedOption == "ALL") {
				if (!isOnline) {
					let allFlagBags = await getAllFlagbags(flagBagDB, stationId, isOnline, eventId, search);
					const includeOver = filterAllOvercollectedRef.current;
					const includeUnder = filterAllUndercollectedRef.current;
					const includeSpare = filterAllSpareFlagbagRef.current;
					if (includeOver || includeUnder || includeSpare) {
						allFlagBags = allFlagBags.filter((fb) =>
							matchesCollectStationFilter(fb, stationId, includeOver, includeUnder, includeSpare),
						);
					}

					const startIndex = pageNo * pageSize;
					const endIndex = startIndex + pageSize;
					const convertedFlagbags = allFlagBags.map(fb => {
						const fbAny = fb as any;
						const pendingQueuedStatus = latestPendingActionByBagId.get(fb.id)?.status;
						let currentStatus: StatusCode;
						if (pendingQueuedStatus !== undefined && pendingQueuedStatus !== null) {
							currentStatus = pendingQueuedStatus as StatusCode;
						} else {
							const rawStatus = fb.status || fbAny.currentStatus || fbAny.status;
							if (typeof rawStatus === "string") {
								if (Object.values(StatusCode).includes(rawStatus as StatusCode)) {
									currentStatus = rawStatus as StatusCode;
								} else {
									currentStatus = convertApiStatusToStatusCode(rawStatus);
								}
							} else {
								currentStatus = StatusCode.NOT_ASSIGN;
							}
						}
						const vd = volunteerDisplayFromOfflineDb(fb.id, fb, fbAny);
						return {
							...fb,
							flagbagNum: fb.flagbagNum || fbAny.flagBagNum,
							serverStatus: fbAny.serverStatus || "-",
							currentStatus: currentStatus,
							status: currentStatus,
							volunteerPhone: vd.volunteerPhone,
							volunteerName: vd.volunteerName,
							volunteerId: vd.volunteerId,
							groupName: fb.teamName || fbAny.groupName || "",
							groupID: fb.teamId || fbAny.groupID || "",
							bagType: fb.bagType || "volunteer",
							finalStationId: fb.finalStationId || fb.stationId,
							finalStationName: fb.finalStationName || fbAny.finalStationName || "",
							isCollectByOtherStation: fbAny.isCollectByOtherStation || false,
							isCollectForOtherStation: fbAny.isCollectForOtherStation || false,
							allowedStatusList: fbAny.allowedStatusList || [],
						};
					});
					const paginatedFlagbags = sortRowsBySortModel(convertedFlagbags, sort, getStatusSortIndex).slice(startIndex, endIndex);
					
					setPageRows("ALL", paginatedFlagbags, allFlagBags.length);
					return;
				} else { // online
						let allFlagBags = await getAllFlagbags(flagBagDB, stationId, isOnline, eventId, search);
						if (selectedOption === "ALL") {
							const includeOver = filterAllOvercollectedRef.current;
							const includeUnder = filterAllUndercollectedRef.current;
							const includeSpare = filterAllSpareFlagbagRef.current;
							if (includeOver || includeUnder || includeSpare) {
								allFlagBags = allFlagBags.filter((fb) =>
									matchesCollectStationFilter(fb, stationId, includeOver, includeUnder, includeSpare),
								);
							}
						}

						const startIndex = pageNo * pageSize;
						const endIndex = startIndex + pageSize;
						const convertedFlagbags = allFlagBags.map(fb => {
							const fbAny = fb as any;
							// 获取原始状态值 - 优先使用 fb.status（API 返回的原始状态）
							const rawStatus = fb.status || fbAny.currentStatus || fbAny.status;
							
							// 转换状态：如果已经是 StatusCode 枚举值，直接使用；否则转换
							let statusCode: StatusCode;
							if (typeof rawStatus === 'string') {
								// 检查是否是 StatusCode 枚举值（StatusCode.NOT_ASSIGN 的值是 "unassigned"）
								if (Object.values(StatusCode).includes(rawStatus as StatusCode)) {
									statusCode = rawStatus as StatusCode;
								} else {
									// 转换 API 状态字符串（如 "unassigned" -> StatusCode.NOT_ASSIGN）
									statusCode = convertApiStatusToStatusCode(rawStatus);
									// 调试：如果转换后的状态与原始状态不匹配，记录警告
									if (rawStatus === 'unassigned' && statusCode !== StatusCode.NOT_ASSIGN) {
										console.warn('Status conversion mismatch:', {
											flagbagNum: fb.flagbagNum || fbAny.flagBagNum,
											rawStatus: rawStatus,
											convertedStatus: statusCode,
										});
									}
								}
							} else {
								statusCode = StatusCode.NOT_ASSIGN;
							}
							const currentStatus = statusCode;
							return {
								...fb,
								flagbagNum: fb.flagbagNum || fbAny.flagBagNum,
								serverStatus: fbAny.serverStatus || "-",
								currentStatus: currentStatus,
								status: currentStatus,
								volunteerPhone: fbAny.volunteerPhone || "",
								volunteerName: fb.volunteerName || "",
								groupName: fb.teamName || fbAny.groupName || "",
								groupID: fb.teamId || fbAny.groupID || "",
								bagType: fb.bagType || "volunteer",
								finalStationId: fb.finalStationId || fb.stationId,
								finalStationName: fb.finalStationName || fbAny.finalStationName || "",
								isCollectByOtherStation: fbAny.isCollectByOtherStation || false,
								isCollectForOtherStation: fbAny.isCollectForOtherStation || false,
								allowedStatusList: fbAny.allowedStatusList || [],
							};
						});
						const paginatedFlagbags = sortRowsBySortModel(convertedFlagbags, sort, getStatusSortIndex).slice(startIndex, endIndex);
						
						setPageRows("ALL", paginatedFlagbags, allFlagBags.length);
				}
			}

		} catch (error) {
			console.error("加載頁面數據失敗:", error);
			enqueueSnackbar("加載數據失敗", { variant: "error" });
		} finally {
			setIsLoadingPage(false);
		}
	};

	const loadPageDataRef = React.useRef(loadPageData);
	const listReloadParamsRef = React.useRef({
		page: paginationModel.page,
		pageSize: paginationModel.pageSize,
		search: apiSearchText,
		sortModel,
	});
	const canReloadListRef = React.useRef(false);
	const statusSavedReloadTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

	React.useLayoutEffect(() => {
		loadPageDataRef.current = loadPageData;
		listReloadParamsRef.current = {
			page: paginationModel.page,
			pageSize: paginationModel.pageSize,
			search: apiSearchText,
			sortModel,
		};
		canReloadListRef.current = Boolean(data.getStationId() && isInit);
	});

	React.useEffect(() => {
		if (typeof window === "undefined") return;

		const onFlagBagStatusSaved = () => {
			void refreshPendingTabCountsRef.current();
			if (!canReloadListRef.current) return;
			if (statusSavedReloadTimerRef.current) clearTimeout(statusSavedReloadTimerRef.current);
			statusSavedReloadTimerRef.current = setTimeout(() => {
				statusSavedReloadTimerRef.current = null;
				const p = listReloadParamsRef.current;
				loadPageDataRef.current(p.page, p.pageSize, undefined, p.search, p.sortModel);
			}, 120);
		};

		window.addEventListener(FLAGBAG_STATUS_SAVED_EVENT, onFlagBagStatusSaved);
		return () => {
			window.removeEventListener(FLAGBAG_STATUS_SAVED_EVENT, onFlagBagStatusSaved);
			if (statusSavedReloadTimerRef.current) clearTimeout(statusSavedReloadTimerRef.current);
		};
	}, []);

	// On mount: reload after 1s
	React.useEffect(() => {
		const timeoutId = setTimeout(() => {
			void refreshPendingTabCountsRef.current();
			const stationId = data.getStationId();
			if (!stationId) return;
			const p = listReloadParamsRef.current;
			void loadPageDataRef.current(p.page, p.pageSize, undefined, p.search, p.sortModel);
		}, 1000);
		return () => clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// App resume (tab/app foreground): reload after 1s
	React.useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout> | null = null;

		const handleVisibilityChange = () => {
			if (document.visibilityState !== "visible") {
				if (timeoutId) {
					clearTimeout(timeoutId);
					timeoutId = null;
				}
				return;
			}

			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			timeoutId = setTimeout(() => {
				timeoutId = null;
				void refreshPendingTabCountsRef.current();
				if (!canReloadListRef.current) return;
				const p = listReloadParamsRef.current;
				void loadPageDataRef.current(p.page, p.pageSize, undefined, p.search, p.sortModel);
			}, 1000);
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, []);

	React.useEffect(() => {
		return () => {
			if (searchKeyupDebounceTimerRef.current) {
				clearTimeout(searchKeyupDebounceTimerRef.current);
			}
		};
	}, []);

	React.useEffect(() => {
		const stationId = data.getStationId();
		if (stationId) {
			setIsInit(true);
			setIsLoading(false);
		}
	}, [data.getStationId()]);

	React.useEffect(() => {
		const stationId = data.getStationId();

		if (stationId && isInit) {
			loadPageData(paginationModel.page, paginationModel.pageSize);
		}
	}, [isInit, data.getStationId(), isOnline]);

	React.useEffect(() => {
		if ((selectedTab === "PENDING_SYNC" || selectedTab === "PENDING_PROCESS") && isInit) {
			loadPageData(paginationModel.page, paginationModel.pageSize, undefined, apiSearchText);
		}
	}, [pendingSyncIds, pendingProcessIds]);

	const handleSortModelChange = (newSortModel: GridSortModel) => {
		setSortModel(newSortModel);
		sortModelRef.current = newSortModel;

		const nextPaginationModel = {
			page: 0,
			pageSize: paginationModelRef.current.pageSize,
		};
		setPaginationModel(nextPaginationModel);
		paginationModelRef.current = nextPaginationModel;

		loadPageDataRef.current(
			0,
			nextPaginationModel.pageSize,
			selectedTabRef.current,
			apiSearchTextRef.current,
			newSortModel,
		);
	};

	const handleGridCellClick = (tabValue: string, row: any) => {
		if (tabValue === "PENDING_PROCESS") {
			const conflicts = loadConflictsFromStorage();
			const conflictItem = conflicts?.find((c: ConflictItem) => c.flagBagId === row.row.id);

			setFlagBagDetail({
				volunteerName: row.row.volunteerName || "",
				volunteerStationName: "",
				teamName: row.row.groupName || row.row.teamName || "",
				phone: "",
				email: "",
				remarks: "",
				flagBagId: row.row.id,
				flagbagNum: row.row.flagbagNum,
				serverStatus: row.row.serverStatus || conflictItem?.serverStatus || "",
				localStatus: row.row.localStatus || conflictItem?.localStatus || "",
				serverUpdateTime: conflictItem?.serverTimestamp != null ? String(conflictItem.serverTimestamp) : "",
				serverUpdateBy: "",
			});
			openModal('conflictSelect', {
				flagBagId: row.row.id,
				flagbagNum: row.row.flagbagNum,
				volunteerName: row.row.volunteerName || "",
				phone: row.row.volunteerPhone || "",
				status: row.row.status,
				stationName: data.getStationName(),
				serverStatus: row.row.serverStatus || conflictItem?.serverStatus || "",
				localStatus:conflictItem?.localStatus ||  row.row.localStatus || "",
				serverUpdateTime: conflictItem?.serverTimestamp != null ? String(conflictItem.serverTimestamp) : "",
				serverUpdateBy: "",
			});
		} else if (row.field === "volunteerName") {
			const conflicts = loadConflictsFromStorage();
			const conflictItem = conflicts?.find((c: ConflictItem) => c.flagBagId === row.row.id);

			// 获取志愿者详细信息
			const getVolunteerDetails = async () => {
				let volunteerStationName = "";
				let email = "";
				let mobileNum = "";
				let remarks = "";
				
				if (row.row.volunteerId) {
					try {
						const eventId = data.getEventId();
						if (eventId) {
							const volunteers = await volunteerController.getVolunteersFromCache(eventId);
							const volunteer = volunteers.find((v: any) => v.volunteerId === row.row.volunteerId);
							
							if (volunteer) {
								email = volunteer.email || "";
								mobileNum = volunteer.mobileNum || "";
								remarks = (volunteer as any).remarks || "";
								// 获取志愿者所属站点名称
								if (volunteer.flagbagStationId) {
									const stationList = data.getStationList();
									const station = stationList?.find((s: any) => s.id === volunteer.flagbagStationId);
									volunteerStationName = station?.stationName || "";
								}
							}
						}
					} catch (error) {
						console.error("获取志愿者详细信息失败:", error);
					}
				}

				const detailData = {
					volunteerName: row.row.volunteerName || "",
					volunteerStationName: volunteerStationName,
					teamName: row.row.groupName || row.row.teamName || "",
					phone: mobileNum,
					email: email,
					remarks: remarks,
					flagBagId: row.row.id,
					flagbagNum: row.row.flagbagNum,
					serverStatus: conflictItem?.serverStatus || "",
					localStatus: conflictItem?.localStatus || "",
					serverUpdateTime: conflictItem?.serverTimestamp ? new Date(conflictItem.serverTimestamp).toLocaleString() : "",
					serverUpdateBy: "",
				};

				setFlagBagDetail(detailData);
				openModal('flagbagDetail', detailData);
			};

			getVolunteerDetails();
		}
	};

	return (
		<Main
			title="旗袋數據"
			titleAlign="left"
			titleIcon={<DehazeIcon />}>
			<Backdrop
				sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}
				open={isLoading}>
				<Paper sx={{ display: "flex", gap: 2, alignItems: "center", py: 2, px: 3 }}>
					<CircularProgress color="primary" />
					<Typography>{"正在更新..."}</Typography>
				</Paper>
			</Backdrop>
			<SyncBar
				handleSubmit={handleRefresh}
				pendingSyncCount={pendingSyncCount}
				pendingProcessCount={pendingProcessCount}
			/>
			<Box
				sx={{ mt: 3, pb: { lg: "5rem"} }}
			>
				{/* <Box sx={{ px: 1, mb: -2, paddingTop: 1 }}>
					<DataUpgradeCard onUpdateSuccess={handleRefresh} />
				</Box> */}
				{/* <Box sx={{ px: 1 }}>
					<Stack
						direction="row"
						spacing={1}
						width={1}
						overflow={"auto"}
						p={1}>
						{tagOptions
							//.sort((a, b) => (!selectedTag.includes(a.value) || selectedTag.includes(b.value) ? 1 : -1))
							.map((option) => (
								<Chip
									key={option.value}
									label={option.label}
									variant={selectedTag.includes(option.value) ? "filled" : "outlined"}
									color="primary"
									onClick={() => handleTagClick(option.value)}
								/>
							))}
					</Stack>
				</Box> */}
				<Box sx={{ px: 2, py: 1 }}>
					<Stack
						direction="row"
						spacing={1.5}
						width={1}
						overflow={"auto"}
						sx={{
							'&::-webkit-scrollbar': {
								display: 'none'
							},
							msOverflowStyle: 'none',
							scrollbarWidth: 'none',
						}}>
						{tabs.map((tab) => (
							<TagChip
								key={tab.value}
								label={tab.label}
								color={tab.color}
								selected={tab.value === selectedTab}
								count={tab.count}
								onClick={() => {
									setSelectedTab(tab.value);
									closeAllModals();
									setSortModel(DEFAULT_SORT_MODEL);
									sortModelRef.current = DEFAULT_SORT_MODEL;
									setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
									loadPageData(0, paginationModel.pageSize, tab.value, apiSearchText, DEFAULT_SORT_MODEL);
								}}
							/>
						))}
					</Stack>
				</Box>
				<Box sx={{ px: 2, py: 1 }}>
					<TextField
						fullWidth
						placeholder="按旗袋編號/義工名稱/義工郵箱/旗站搜尋"
						value={apiSearchText}
						onChange={(e) => {
							const v = e.target.value;
							apiSearchTextRef.current = v;
							setApiSearchText(v);
						}}
						onKeyUp={(e) => {
							if (e.key === "Enter") return;
							const skipSearchKey =
								e.key === "Tab" ||
								e.key === "Escape" ||
								e.key === "Shift" ||
								e.key === "Control" ||
								e.key === "Alt" ||
								e.key === "Meta" ||
								e.key.startsWith("Arrow") ||
								e.key === "Home" ||
								e.key === "End" ||
								e.key === "PageUp" ||
								e.key === "PageDown";
							if (skipSearchKey) return;
							if (searchKeyupDebounceTimerRef.current) {
								clearTimeout(searchKeyupDebounceTimerRef.current);
							}
							searchKeyupDebounceTimerRef.current = setTimeout(() => {
								searchKeyupDebounceTimerRef.current = null;
								setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
								loadPageDataRef.current(
									0,
									paginationModelRef.current.pageSize,
									selectedTabRef.current,
									apiSearchTextRef.current,
								);
							}, 1000);
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								if (searchKeyupDebounceTimerRef.current) {
									clearTimeout(searchKeyupDebounceTimerRef.current);
									searchKeyupDebounceTimerRef.current = null;
								}
								setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
								loadPageData(0, paginationModel.pageSize, selectedTab, apiSearchText);
							}
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon />
								</InputAdornment>
							),
						}}
						sx={{
							'& .MuiOutlinedInput-root': {
								borderRadius: 2,
							},
						}}
					/>
				</Box>

				<Stack spacing={2} sx={{ width: 1 }}>
					{selectedTab === "ALL" && (
						<Box sx={{ width: 1, minWidth: 0 }}>
							<Stack direction="row" spacing={2} sx={{ px: 2, pb: 1, flexWrap: "wrap" }}>
								<FormControlLabel
									control={
										<Checkbox
											size="small"
											checked={filterAllOvercollected}
											onChange={(_, checked) => {
												setFilterAllOvercollected(checked);
												filterAllOvercollectedRef.current = checked;
												setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
												loadPageData(0, paginationModel.pageSize, "ALL", apiSearchText);
											}}
										/>
									}
									label="多收"
								/>
								<FormControlLabel
									control={
										<Checkbox
											size="small"
											checked={filterAllUndercollected}
											onChange={(_, checked) => {
												setFilterAllUndercollected(checked);
												filterAllUndercollectedRef.current = checked;
												setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
												loadPageData(0, paginationModel.pageSize, "ALL", apiSearchText);
											}}
										/>
									}
									label="少收"
								/>
								<FormControlLabel
									control={
										<Checkbox
											size="small"
											checked={filterAllSpareFlagbag}
											onChange={(_, checked) => {
												setFilterAllSpareFlagbag(checked);
												filterAllSpareFlagbagRef.current = checked;
												setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
												loadPageData(0, paginationModel.pageSize, "ALL", apiSearchText);
											}}
										/>
									}
									label="後備"
								/>
							</Stack>
							<DataGrid
								columns={normalColumns}
								rows={rowsAll}
								getRowId={(row) => row.id}
								rowHeight={60}
								disableColumnMenu
								disableRowSelectionOnClick
								pagination
								paginationMode="server"
								sortingMode="server"
								paginationModel={paginationModel}
								sortModel={sortModel}
								rowCount={totalRowsAll}
								onPaginationModelChange={(newModel) => {
									setPaginationModel(newModel);
									loadPageData(newModel.page, newModel.pageSize, "ALL", apiSearchText);
								}}
								onSortModelChange={handleSortModelChange}
								pageSizeOptions={[5, 10, 25, 50, 100]}
								localeText={{ noRowsLabel: "沒有數據" }}
								autoHeight
								loading={isLoadingPage || !isInit}
								getRowClassName={(params) => {
									const rowId = params.row.id;
									if (pendingProcessIds.has(rowId)) return "pending-process-row";
									if (pendingSyncIds.has(rowId)) return "pending-sync-row";
									return "";
								}}
								sx={{
									'& .MuiDataGrid-footerContainer': { justifyContent: 'center' },
									'& .MuiTablePagination-root': { width: '100%' },
									'& .MuiTablePagination-toolbar': { justifyContent: 'center' },
									'& .MuiTablePagination-spacer': { display: 'none' },
									'& .pending-sync-row': {
										backgroundColor: 'rgba(255, 137, 20, 0.08)',
										'&:hover': { backgroundColor: 'rgba(255, 137, 20, 0.12)' },
									},
									'& .pending-process-row': {
										backgroundColor: 'rgba(255, 38, 38, 0.08)',
										'&:hover': { backgroundColor: 'rgba(255, 38, 38, 0.12)' },
									},
								}}
								onCellClick={(row) => handleGridCellClick("ALL", row)}
							/>
						</Box>
					)}
					{selectedTab === "PENDING_SYNC" && (
						<Box sx={{ width: 1, minWidth: 0 }}>
							<DataGrid
								columns={pendingSyncColumns}
								rows={rowsPendingSync}
								getRowId={(row) => row.id}
								rowHeight={60}
								disableColumnMenu
								disableRowSelectionOnClick
								pagination
								paginationMode="server"
								sortingMode="server"
								paginationModel={paginationModel}
								sortModel={sortModel}
								rowCount={totalRowsPendingSync}
								onPaginationModelChange={(newModel) => {
									setPaginationModel(newModel);
									loadPageData(newModel.page, newModel.pageSize, "PENDING_SYNC", apiSearchText);
								}}
								onSortModelChange={handleSortModelChange}
								pageSizeOptions={[5, 10, 25, 50, 100]}
								localeText={{ noRowsLabel: "沒有數據" }}
								autoHeight
								loading={isLoadingPage || !isInit}
								getRowClassName={(params) => {
									const rowId = params.row.id;
									if (pendingProcessIds.has(rowId)) return "pending-process-row";
									if (pendingSyncIds.has(rowId)) return "pending-sync-row";
									return "";
								}}
								sx={{
									'& .MuiDataGrid-footerContainer': { justifyContent: 'center' },
									'& .MuiTablePagination-root': { width: '100%' },
									'& .MuiTablePagination-toolbar': { justifyContent: 'center' },
									'& .MuiTablePagination-spacer': { display: 'none' },
									'& .pending-sync-row': {
										backgroundColor: 'rgba(255, 137, 20, 0.08)',
										'&:hover': { backgroundColor: 'rgba(255, 137, 20, 0.12)' },
									},
									'& .pending-process-row': {
										backgroundColor: 'rgba(255, 38, 38, 0.08)',
										'&:hover': { backgroundColor: 'rgba(255, 38, 38, 0.12)' },
									},
								}}
								onCellClick={(row) => handleGridCellClick("PENDING_SYNC", row)}
							/>
						</Box>
					)}
					{selectedTab === "PENDING_PROCESS" && (
						<Box sx={{ width: 1, minWidth: 0 }}>
							<DataGrid
								columns={pendingProcessColumns}
								rows={rowsPendingProcess}
								getRowId={(row) => row.id}
								rowHeight={60}
								disableColumnMenu
								disableRowSelectionOnClick
								pagination
								paginationMode="server"
								sortingMode="server"
								paginationModel={paginationModel}
								sortModel={sortModel}
								rowCount={totalRowsPendingProcess}
								onPaginationModelChange={(newModel) => {
									setPaginationModel(newModel);
									loadPageData(newModel.page, newModel.pageSize, "PENDING_PROCESS", apiSearchText);
								}}
								onSortModelChange={handleSortModelChange}
								pageSizeOptions={[5, 10, 25, 50, 100]}
								localeText={{ noRowsLabel: "沒有數據" }}
								autoHeight
								loading={isLoadingPage || !isInit}
								getRowClassName={(params) => {
									const rowId = params.row.id;
									if (pendingProcessIds.has(rowId)) return "pending-process-row";
									if (pendingSyncIds.has(rowId)) return "pending-sync-row";
									return "";
								}}
								sx={{
									'& .MuiDataGrid-footerContainer': { justifyContent: 'center' },
									'& .MuiTablePagination-root': { width: '100%' },
									'& .MuiTablePagination-toolbar': { justifyContent: 'center' },
									'& .MuiTablePagination-spacer': { display: 'none' },
									'& .pending-sync-row': {
										backgroundColor: 'rgba(255, 137, 20, 0.08)',
										'&:hover': { backgroundColor: 'rgba(255, 137, 20, 0.12)' },
									},
									'& .pending-process-row': {
										backgroundColor: 'rgba(255, 38, 38, 0.08)',
										'&:hover': { backgroundColor: 'rgba(255, 38, 38, 0.12)' },
									},
								}}
								onCellClick={(row) => handleGridCellClick("PENDING_PROCESS", row)}
							/>
						</Box>
					)}
				</Stack>
			</Box>
			
			
			{/* Render modals from stack */}
			{modalStack.map((modal, index) => {
				if (modal.type === 'flagbagDetail') {
					const detailData = modal.data || flagBagDetail;
					return (
						<Dialog
							key={`modal-${index}`}
							open={true}
							onClose={closeModal}
							sx={{ "& .MuiDialog-paper": { minWidth: "300px", maxWidth: "500px" } }}>
							<DialogTitle>義工詳情</DialogTitle>
							<DialogContent>
								<Stack spacing={1.5} sx={{ mt: 1 }}>
									<Box>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
											義工姓名
										</Typography>
										<Typography variant="body1">
											{detailData.volunteerName || "-"}
										</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
											旗站
										</Typography>
										<Typography variant="body1">
											{detailData.volunteerStationName || "-"}
										</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
											團隊
										</Typography>
										<Typography variant="body1">
											{detailData.teamName || "-"}
										</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
											電話號碼
										</Typography>
										<Typography variant="body1">
											{detailData.phone || "-"}
										</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
											電郵地址
										</Typography>
										<Typography variant="body1">
											{detailData.email || "-"}
										</Typography>
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
											備注
										</Typography>
										<Typography variant="body1">
											{detailData.remarks || "-"}
										</Typography>
									</Box>
								</Stack>
							</DialogContent>
						</Dialog>
					);
				}
				
				if (modal.type === 'conflictSelect') {
					const conflictData = modal.data || flagBagDetail;
					console.log(conflictData, flagBagDetail);
					return (
						<ConflictSelectModals
							key={`modal-${index}`}
							open={true}
							isOnline={isOnline}
							flagBagId={conflictData.flagBagId}
							flagbagNum={conflictData.flagbagNum}
							serverFlagbagStatus={conflictData.status}
							serverUpdateTime={conflictData.serverUpdateTime}
							serverUpdateBy={conflictData.serverUpdateBy || ""}
							localFlagbagStatus={conflictData.localStatus}
							onClose={closeModal}
							onSuccess={async () => {
								try {
									const pendingSyncIdsSet = new Set<string>();
									try {
										const allPendingActions = await getOfflinePendingActionsSafe();
										if (allPendingActions && Array.isArray(allPendingActions)) {
											allPendingActions.forEach((action: any) => {
												if (action.flagBagID && !action.syncError) {
													pendingSyncIdsSet.add(action.flagBagID);
												}
											});
										}
									} catch (error) {
										console.error("获取待同步数据失败:", error);
									}
									setPendingSyncIds(pendingSyncIdsSet);
									setPendingSyncCount(pendingSyncIdsSet.size);

									const pendingProcessIdsSet = new Set<string>();
									const updatedConflicts = loadConflictsFromStorage();
									if (updatedConflicts && Array.isArray(updatedConflicts)) {
										updatedConflicts.forEach((conflict: ConflictItem) => {
											if (conflict.flagBagId) {
												pendingProcessIdsSet.add(conflict.flagBagId);
											}
										});
									}
									try {
										const allPendingActions = await getOfflinePendingActionsSafe();
										if (allPendingActions && Array.isArray(allPendingActions)) {
											allPendingActions.forEach((action: any) => {
												if (action.flagBagID && action.syncError) {
													pendingProcessIdsSet.add(action.flagBagID);
												}
											});
										}
									} catch (error) {
										console.error("获取同步失败数据失败:", error);
									}
									setPendingProcessIds(pendingProcessIdsSet);
									setPendingProcessCount(pendingProcessIdsSet.size);

									await loadPageData(paginationModel.page, paginationModel.pageSize, undefined, apiSearchText);
								} catch (error) {
									console.error("更新數據失敗:", error);
								}
							}}
						/>
					);
				}
				
				if (modal.type === 'statusUpdate') {
					const statusData = modal.data || editingFlagBag;
					if (!statusData) return null;
					return (
						<StatusUpdateModals
							key={`modal-${index}`}
							open={true}
							flagbagNum={statusData.flagbagNum || ""}
							currentStatus={statusData.currentStatus}
							statusOptions={filterOptions(statusData.currentStatus)}
							onClose={closeModal}
							onConfirm={(newStatus) => {
								if (statusData.id) {
									handleStatusChangeWithCode(statusData.id, newStatus);
								}
							}}
						/>
					);
				}

				if (modal.type === 'reservedForm') {
					return (
						<ReservedForm
							key={`modal-${index}`}
							open={true}
							onClose={closeModal}
							assigningFlagBagId={modal.data?.flagBagId}
							handleSubmit={handleSubmit}
						/>
					);
				}
				
				return null;
			})}
		</Main>
	);
};

export default FlagBagList;
